// Form fields in the soft "pressed-in" style of the app: text and select.
import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from '@/components/ui/Icon';
import { cx } from '@/lib/format';

/** `keepHint` — the hint holds actions the error refers to (e.g. «не помню цену»): keep it under the error */
type FieldShell = { label: string; error?: string | null; hint?: ReactNode; keepHint?: boolean; children: (id: string) => ReactNode };
function Shell({ label, error, hint, keepHint, children }: FieldShell) {
  const id = useId();
  return (
    <div className={cx('pf-field', error && 'bad')}>
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {error && <span className="pf-err" role="alert">{error}</span>}
      {(!error || keepHint) && hint && <span className="pf-hint">{hint}</span>}
    </div>
  );
}

type TextProps = { label: string; error?: string | null; hint?: ReactNode; keepHint?: boolean } & InputHTMLAttributes<HTMLInputElement>;
export const TextField = ({ label, error, hint, keepHint, ...rest }: TextProps) => (
  <Shell label={label} error={error} hint={hint} keepHint={keepHint}>{(id) => <input id={id} className="pf-in" aria-invalid={!!error} {...rest} />}</Shell>
);

export function SelectField({ label, value, onChange, options, hint }: { label: string; value: string; onChange: (v: string) => void; options: Array<[string, string]>; hint?: ReactNode }) {
  return (
    <Shell label={label} hint={hint}>
      {(id) => (
        <span className="pf-sel">
          <select id={id} className="pf-in" value={value} onChange={(e) => onChange(e.target.value)}>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Icon name="chev" />
        </span>
      )}
    </Shell>
  );
}
